import React, { FC } from 'react';
import { useFormikContext } from 'formik';
import { Button, Asset, InfoIcon } from '@clearcover/atlas';
import { ButtonGroup, FormInput } from 'Shared/components';
import { ClientOptionsType } from 'QuoteApp/services/endpoints/GetConfig';
import { useEdit } from 'QuoteApp/utils/contexts';
import styles from './styles.module.css';

type ChallengeFormCommonProps = {
  identifier: string;
  type:
    | 'current-carrier'
    | 'drivers-licence'
    | 'accidents'
    | 'violations'
    | 'prior-policies';
  isAdding?: boolean;
  onCancelAdd?: () => void;
};

type ChallengeFormVariantType =
  | {
      variant: 'remove';
      challengeReasonConfig: ClientOptionsType;
    }
  | {
      variant: 'edit';
      includeRemove?: boolean;
      onRemove?: () => void;
    };

type ChallengeFormValuesType = Record<
  string,
  {
    challengeReason?: string;
    reasonText: string;
  }
>;

type ChallengeFormPropsType = ChallengeFormVariantType &
  ChallengeFormCommonProps & {
    disableSubmit?: boolean;
  };

export const ChallengeForm: FC<ChallengeFormPropsType> = props => {
  const {
    isSubmitting,
    resetForm,
    setFieldValue,
    submitForm,
    values,
    touched,
    errors,
  } = useFormikContext<ChallengeFormValuesType>();

  const { changeEditingId } = useEdit();

  return (
    <div>
      {props.variant === 'remove' && (
        <FormInput
          variant="selectAutocomplete"
          options={props.challengeReasonConfig}
          onChange={(value: string) =>
            setFieldValue(`${props.identifier}.challengeReason`, value)
          }
          value={values[props.identifier].challengeReason}
          label="Reason for removal"
          name={`${props.identifier}.challengeReason`}
          errorMessage={
            touched[props.identifier]?.['challengeReason']
              ? errors[props.identifier]?.['challengeReason']
              : undefined
          }
        />
      )}
      <FormInput
        data-testid="reason-text"
        variant="text"
        name={`${props.identifier}.reasonText`}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setFieldValue(
            `${props.identifier}.reasonText`,
            e.currentTarget.value
          );
        }}
        label="Notes"
        value={values[props.identifier].reasonText}
        errorMessage={errors[props.identifier]?.reasonText}
      />
      <div className={styles.info}>
        <Asset
          className={styles.infoIcon}
          icon={InfoIcon}
          height={16}
          width={16}
          description="info"
        />
        Please remember to submit proof in Zendesk when
        {props.variant === 'remove' ? ' removing' : ' modifying'} report data.
      </div>
      <div className={styles.action}>
        {!props.isAdding && (
          <div className={styles.warning}>
            {props.variant === 'remove' ? 'Removing' : 'Modifying'} report data
            cannot be undone.
          </div>
        )}
        <ButtonGroup alignRight>
          {props.variant === 'edit' && props.includeRemove && !props.isAdding && (
            <Button
              data-testid={`data-challenge-${props.type}-remove`}
              variant="danger"
              onClick={() => {
                props.onRemove?.();
              }}
            >
              Remove
            </Button>
          )}
          <Button
            data-testid={`data-challenge-${props.type}-cancel`}
            variant="secondary"
            onClick={() => {
              resetForm();
              changeEditingId(null);
              if (props.variant === 'edit' && props.isAdding) {
                props.onCancelAdd?.();
              }
            }}
          >
            Cancel
          </Button>
          <Button
            data-testid={`data-challenge-${props.type}-submit`}
            variant={props.variant === 'remove' ? 'danger' : 'primary'}
            onClick={submitForm}
            isSubmitting={isSubmitting}
            disabled={props.disableSubmit}
          >
            {props.variant === 'remove' ? 'Remove' : 'Submit'}
          </Button>
        </ButtonGroup>
      </div>
    </div>
  );
};
